/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';
import MDSelect from 'components/MDSelect';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  code: '',
  name: '',
  percent_sell: '',
  percent_profit: '',
  percent_quantity: '',
  percent_add: '',
  percent_request: '',
  percent_purchase: '',
  day_export: '',
  day_purchase: '',
  color: '',
  icon: '',
  icon_fa: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const cboColor = [
  { id: 'info', name: 'Blue' },
  { id: 'success', name: 'Green' },
  { id: 'warning', name: 'Orange' },
  { id: 'error', name: 'Red' },
  { id: 'primary', name: 'Pink' },
  { id: 'dark', name: 'Black' },
];

function BestSeller() {
  const mod = 'best_seller';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.id`), align: 'center', width: 40 },
    { id: 'tb.code', label: t(`attr.code`), align: 'left' },
    { id: 'tb.name', label: 'Rating', align: 'left' },
    { id: 'tb.percent_sell', label: 'ยอดการขาย', align: 'right' },
    { id: 'tb.percent_profit', label: 'กำไร', align: 'right' },
    { id: 'tb.percent_quantity', label: 'จำนวนการขาย', align: 'right' },
    { id: 'tb.day_export', label: 'จุดเติมของ', align: 'right' },
    { id: 'tb.percent_add', label: 'ปริมาณการเติม', align: 'right' },
    { id: 'tb.day_purchase', label: 'จุดสั่งซื้อ', align: 'right' },
    { id: 'tb.percent_purchase', label: 'ปริมาณสั่งซื้อ', align: 'right' },
    { id: 'tb.updated_at', label: t(`attr.updated_at`), align: 'center' },
    { id: 'tb.enable', label: t(`attr.enable`), align: 'center', width: 50 },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = (item) => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData('GET', `/${mod}/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 1)) return;
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setConfirm(false);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      code: attr.code,
      name: attr.name,
      enable: attr.enable,
      percent_sell: attr.percent_sell,
      percent_profit: attr.percent_profit,
      percent_quantity: attr.percent_quantity,
      percent_add: attr.percent_add,
      percent_request: attr.percent_request,
      day_export: attr.day_export,
      day_purchase: attr.day_purchase,
      color: attr.color,
      icon: attr.icon,
      icon_fa: attr.icon_fa,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);
    setConfirm(false);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      code: attr.code,
      name: attr.name,
      enable: attr.enable,
      percent_sell: attr.percent_sell,
      percent_profit: attr.percent_profit,
      percent_quantity: attr.percent_quantity,
      percent_add: attr.percent_add,
      percent_request: attr.percent_request,
      day_export: attr.day_export,
      day_purchase: attr.day_purchase,
      color: attr.color,
      icon: attr.icon,
      icon_fa: attr.icon_fa,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const btnEdit = (item) => {
    return (
      <Tooltip title={t(`element.edit`)}>
        <MDButton
          variant='text'
          color='info'
          iconOnly
          onClick={() => open(item)}
          sx={{ fontSize: '1rem', p: 0 }}
        >
          {item.code}
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (!app.perm.del) return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const statusFaDisplay = (item) => {
    return (
      <code className={`color-${item.color}`}>
        <i className={item.icon_fa} /> {item.name}
      </code>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <Tooltip title={t(`element.add`)}>
              <MDButton
                variant='contained'
                color='dark'
                size='large'
                fullWidth
                sx={{ pt: 1.6, pb: 1.6 }}
                onClick={() => {
                  setAttr(initAttr);
                  setConfirm(false);
                  setModalAdd(true);
                }}
              >
                <Icon>post_add</Icon>
              </MDButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell align='left'>{btnEdit(row)}</TableCell>
                      <TableCell align='left'>{row.name}</TableCell>
                      <TableCell align='right' className='color-danger'>
                        {row.percent_sell} %
                      </TableCell>
                      <TableCell align='right' className='color-primary'>
                        {row.percent_profit} %
                      </TableCell>
                      <TableCell align='right' className='color-brown'>
                        {row.percent_quantity} %
                      </TableCell>
                      <TableCell align='right' className='color-orange'>
                        {row.day_export} วัน
                      </TableCell>
                      <TableCell align='right' className='color-success'>
                        {row.percent_add} %
                      </TableCell>
                      <TableCell align='right' className='color-violet'>
                        {row.day_purchase} วัน
                      </TableCell>
                      <TableCell align='right' className='color-info'>
                        {row.percent_purchase} %
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.updated_at)}
                      </TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.code`)} *`}
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='Rating *'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='Icon'
            onChange={(e) => setAttr({ ...attr, icon: e.target.value })}
            value={attr.icon}
            variant='standard'
            fullWidth
            InputProps={{
              endAdornment: (
                <MDButton
                  variant='text'
                  tabIndex={-1}
                  color='dark'
                  size='small'
                  iconOnly
                  href='https://fonts.google.com/icons?icon.set=Material+Icons'
                  target='_blank'
                >
                  <Icon>search</Icon>
                </MDButton>
              ),
              inputProps: {
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='Icon Awesome'
            onChange={(e) => setAttr({ ...attr, icon_fa: e.target.value })}
            value={attr.icon_fa}
            variant='standard'
            fullWidth
            InputProps={{
              endAdornment: (
                <MDButton
                  variant='text'
                  tabIndex={-1}
                  color='dark'
                  size='small'
                  iconOnly
                  href='https://fontawesome.com/v5/search'
                  target='_blank'
                >
                  <Icon>search</Icon>
                </MDButton>
              ),
              inputProps: {
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='สี'
            labelDefault={t(`element.please_select`)}
            data={cboColor}
            value={attr.color}
            onChange={(e) => setAttr({ ...attr, color: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDCheckbox
            label={`${t(`attr.enable`)}`}
            checked={attr.enable === 'Y'}
            onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderData = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='จุดเติมของ'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, day_export: e.target.value })}
            value={attr.day_export}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>วัน</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ปริมาณการเติม'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, percent_add: e.target.value })}
            value={attr.percent_add}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>%</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='จุดสั่งซื้อ'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, day_purchase: e.target.value })}
            value={attr.day_purchase}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>วัน</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ปริมาณสั่งซื้อ'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, percent_purchase: e.target.value })}
            value={attr.percent_purchase}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>%</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ยอดการขาย'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, percent_sell: e.target.value })}
            value={attr.percent_sell}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>%</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='กำไร'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, percent_profit: e.target.value })}
            value={attr.percent_profit}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>%</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='จำนวนการขาย'
            onFocus={(e) => e.target.select()}
            onChange={(e) => setAttr({ ...attr, percent_quantity: e.target.value })}
            value={attr.percent_quantity}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <MDTypography variant='body'>%</MDTypography>
                </InputAdornment>
              ),
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderInfo()}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderData()}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='md' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderInfo()}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderData()}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), t(`menu.best_seller`)]} />
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default BestSeller;
